import type { EntryGateStatus } from '@core/entities/EntryGateStatus';
import type { EntryGateGateway } from '@core/ports/EntryGateGateway';
import { AuthenticatedBffTransport } from '@infrastructure/http/AuthenticatedBffTransport';
import { BffTransportError } from '@infrastructure/http/BffTransportError';

export type EntryGateGatewayErrorCode = 'offline' | 'retry-exhausted' | 'session-required' | 'request-failed';

export class EntryGateGatewayError extends Error {
  public constructor(
    public readonly code: EntryGateGatewayErrorCode,
    message: string
  ) {
    super(message);
    this.name = 'EntryGateGatewayError';
  }
}

export class BffEntryGateHttpGateway implements EntryGateGateway {
  private static readonly offlineMessage = 'Your internet connection appears to be unavailable. Reconnect to the internet, then start again from the NHS App if you still need help.';

  private static readonly connectionFailureMessage = 'Unable to reach Intelligent Navigator right now. Check your connection and try again.';

  public constructor(private readonly transport = new AuthenticatedBffTransport()) {}

  public async getStatus(): Promise<EntryGateStatus> {
    const response = await this.fetchWithConnectionHandling('/api/auth/gate', {
      headers: {
        Accept: 'application/json'
      }
    });

    if (response.status === 401) {
      throw new EntryGateGatewayError('session-required', 'Start this journey from the NHS App to continue.');
    }

    if (!response.ok) {
      throw new EntryGateGatewayError('request-failed', 'Unable to load the Intelligent Navigator entry gate.');
    }

    return (await response.json()) as EntryGateStatus;
  }

  private async fetchWithConnectionHandling(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
    try {
      return await this.transport.fetch(input, init);
    } catch (error) {
      if (error instanceof BffTransportError && error.code === 'offline') {
        throw new EntryGateGatewayError('offline', BffEntryGateHttpGateway.offlineMessage);
      }

      throw new EntryGateGatewayError('request-failed', BffEntryGateHttpGateway.connectionFailureMessage);
    }
  }
}